import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { type MouseEvent } from 'react';

import { Checkmark } from '../../assets/svg/Checkmark';
import OfferIcon from '../../assets/svg/OfferIcon';
import { formatDecimal } from '../../helpers/formatDecimal';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { formatYearsAndMonths } from '../../helpers/formatYearsAndMonths';
import { getOfferTitleTranslationKey } from '../../helpers/getOfferTitleTranslationKey';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { Offers } from '../../types/OfferTypes';
import {
    ContentHeader,
    Contet,
    CustomToggleButton,
    StyledCard,
    StyledCardContent,
    ToggleGroup,
} from './ToggleOfferField.styles';

type Props = {
    offer: Offers | undefined;
    setSelected: (event: MouseEvent<HTMLElement>, offerId: string) => void;
    selected?: string;
};

const ToggleOfferField = ({ offer, setSelected, selected }: Props) => {
    const translate = useTranslation();
    const { countryCode, languageCode } = useSettings();
    const theme = useTheme();
    const oneOffer = offer?.offers && offer.offers.length === 1;
    const isSmallScreen = useMediaQuery('(min-width:600px)');

    const updateSeleted = (event: MouseEvent<HTMLElement>, offerId: string) => {
        if (offerId !== null) {
            setSelected(event, offerId);
        }
    };

    return (
        <ToggleGroup
            value={selected}
            onChange={updateSeleted}
            color="primary"
            exclusive
            disabled={oneOffer}
            isOneOffer={oneOffer}>
            {offer?.offers.map(
                (option, index) =>
                    option.approved.amount !== 0 && (
                        <CustomToggleButton
                            key={index}
                            value={option.offerId}
                            isOneOffer={oneOffer}
                            data-testid={'toggle-button-' + option.offerId}>
                            <StyledCard isOneOffer={oneOffer}>
                                <StyledCardContent>
                                    <ContentHeader>
                                        <OfferIcon offerType={option.offerType} />
                                        <Typography variant="subtitle2" color={'grey.900'}>
                                            {translate(
                                                getOfferTitleTranslationKey(
                                                    option.offerType,
                                                    option.offerType !== 'CASH',
                                                    countryCode,
                                                    languageCode,
                                                ),
                                            )}
                                        </Typography>
                                    </ContentHeader>
                                    <Contet>
                                        <Typography variant="body1" color={theme.palette.text.secondary}>
                                            {translate('field.amount.label')}:
                                        </Typography>
                                        <Typography
                                            variant="h3"
                                            color={'grey.900'}
                                            sx={{
                                                fontSize: isSmallScreen ? '32px !important' : '18px !important',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}>
                                            {`${formatNumberByThousands(
                                                formatDecimal(option.approved.amount, countryCode),
                                            )} ${translate('field.currency.label')}`}
                                        </Typography>
                                        <Typography variant="body2">
                                            {`${translate('field.toggle.tenor.label')} ${formatYearsAndMonths(
                                                Number(option.tenor.months),
                                                translate,
                                            )}`}
                                        </Typography>
                                    </Contet>
                                    <Typography variant="body2" color={'grey.900'} textAlign={'right'}>
                                        {option.offerId === selected && offer.offers.length > 1 && <Checkmark />}
                                    </Typography>
                                </StyledCardContent>
                            </StyledCard>
                        </CustomToggleButton>
                    ),
            )}
        </ToggleGroup>
    );
};

export default ToggleOfferField;
